import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ArrowLeft, Save, Shield, Check, Trash2,
    ChevronDown, ChevronUp, FolderTree, Layers,
    Settings, Users, Loader2
} from 'lucide-react';

const Panel = ({ children, className = "" }) => (
    <div className={`rounded-2xl p-4 sm:p-5 transition-all bg-white ${className}`}
        style={{ border: '1px solid rgba(255,125,56,0.2)', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
        {children}
    </div>
);

const PermissionCheckbox = ({ label, checked, onChange, color }) => (
    <label className="flex items-center gap-2 cursor-pointer select-none">
        <div className="relative flex items-center">
            <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" />
            <div className="w-4 h-4 rounded border transition-colors peer-checked:border-transparent"
                style={{ borderColor: checked ? color : '#d1d5db', backgroundColor: checked ? color : 'transparent' }}>
                {checked && <Check size={12} className="text-white mx-auto mt-0.5" />}
            </div>
        </div>
        <span className="text-xs font-medium" style={{ color: color || '#4a4a4a' }}>{label}</span>
    </label>
);

const PermissionSelector = ({ componentKey, componentData, onToggle, onSelectAll, onClearAll, depth = 0 }) => {
    const [expanded, setExpanded] = useState(true);
    const isSection = componentData.Sections && !componentData.ComponentName;
    const componentName = componentData.ComponentName || componentData.title || componentKey;
    const availableActions = componentData.Actions || [];
    const selectedActions = componentData.SelectedActions || [];
    const totalAvailable = availableActions.length;
    const totalSelected = selectedActions.length;
    const hasSome = totalSelected > 0;
    const allIndividualSelected = availableActions.filter(a => a !== 'All').every(a => selectedActions.includes(a));
    const isFullyChecked = selectedActions.includes('All') || (totalAvailable > 0 && allIndividualSelected);

    return (
        <div className={`rounded-xl overflow-hidden bg-white border border-orange-200 ${depth > 0 ? 'ml-4 sm:ml-6' : ''}`}>
            <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-orange-50 transition-colors"
                onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-orange-50">
                        {isSection ? <FolderTree size={16} style={{ color: '#FF7D38' }} /> : <Layers size={16} style={{ color: '#FF7D38' }} />}
                    </div>
                    <div>
                        <span className="text-sm font-medium text-gray-800">{componentName}</span>
                        {!isSection && <p className="text-[10px] text-gray-500">{totalSelected}/{totalAvailable} permission{totalAvailable !== 1 ? 's' : ''}</p>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {isFullyChecked && !isSection && <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">Full Access</span>}
                    {hasSome && !isFullyChecked && !isSection && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-50 text-orange-500 border border-orange-200">Partial</span>}
                    {!hasSome && !isSection && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200">No Access</span>}
                    {expanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                </div>
            </div>

            {expanded && !isSection && (
                <div className="p-3 pt-0 border-t border-orange-100">
                    <div className="flex flex-wrap items-center gap-4 mt-3">
                        {['Add', 'Reply', 'View', 'Edit', 'Delete', 'Download', 'All'].map(action =>
                            availableActions.includes(action) && (
                                <PermissionCheckbox key={action} label={action}
                                    checked={selectedActions.includes(action)}
                                    onChange={() => onToggle(componentKey, action)}
                                    color={{ Add: '#22c55e', Reply: '#8b5cf6', View: '#3b82f6', Edit: '#FF7D38', Delete: '#ef4444', Download: '#8b5cf6', All: '#FF7D38' }[action]} />
                            )
                        )}
                        <div className="flex-1" />
                        <button type="button" onClick={(e) => { e.stopPropagation(); onSelectAll(componentKey); }}
                            className="text-[10px] px-2 py-1 rounded hover:bg-orange-50 text-orange-500 font-medium">Select All</button>
                        <button type="button" onClick={(e) => { e.stopPropagation(); onClearAll(componentKey); }}
                            className="text-[10px] px-2 py-1 rounded hover:bg-gray-50 text-gray-500 font-medium">Clear All</button>
                    </div>
                    <div className="mt-2 text-[10px] text-gray-500">
                        {isFullyChecked && <span className="text-green-600">✓ Full access to {componentName}</span>}
                        {hasSome && !isFullyChecked && <span>Access: {selectedActions.filter(a => a !== 'All').join(', ')}</span>}
                        {!hasSome && <span>No access to {componentName}</span>}
                    </div>
                </div>
            )}

            {expanded && isSection && componentData.Sections && (
                <div className="p-3 pt-0 border-t border-orange-100">
                    <div className="space-y-2 mt-3">
                        {componentData.Sections.map((section, index) => (
                            <PermissionSelector key={section.ComponentName || section.title || `section-${index}`}
                                componentKey={section.ComponentName || section.title || `section-${index}`}
                                componentData={section} onToggle={onToggle} onSelectAll={onSelectAll} onClearAll={onClearAll} depth={depth + 1} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


const API_BASE = 'https://api.mobilink.tech/api/superAdmin';
const API_TOKEN = sessionStorage.getItem('adminToken') ||
    sessionStorage.getItem('subAdminToken') || '';
const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN}` };

const unwrapPermissions = (raw) => {
    if (Array.isArray(raw)) return raw[0] || {};
    return raw || {};
};

const wrapPermissions = (obj) => [obj];

const STANDARD_ACTIONS = ['Add', 'Reply', 'View', 'Edit', 'Delete', 'Download', 'All'];

const transformForAPI = (permsObj) => {
    const result = {};
    for (const [key, value] of Object.entries(permsObj)) {
        if (value.Sections) {
            result[key] = {
                title: value.title || value.ComponentName,
                ...(value.ComponentName ? { ComponentName: value.ComponentName } : {}),
                Sections: value.Sections.map(s => {
                    const t = transformForAPI({ temp: s });
                    return t.temp;
                })
            };
        } else {
            result[key] = { ComponentName: value.ComponentName, Actions: [...(value.SelectedActions || [])] };
        }
    }
    return result;
};

const findSectionMatchIndex = (apiSections, section, index) => {
    if (!apiSections || apiSections.length === 0) return -1;
    const key = section.ComponentName || section.title;
    if (key) {
        const namedIndex = apiSections.findIndex(s => (s.ComponentName || s.title) === key);
        if (namedIndex !== -1) return namedIndex;
    }
    return apiSections[index] ? index : -1;
};


const buildFromSavedOnly = (savedValue) => {
    if (!savedValue) return null;
    if (savedValue.Sections) {
        const builtSections = savedValue.Sections.map(s => buildFromSavedOnly(s)).filter(Boolean);
        if (builtSections.length === 0) return null;
        return {
            title: savedValue.title,
            ...(savedValue.ComponentName ? { ComponentName: savedValue.ComponentName } : {}),
            Sections: builtSections
        };
    }
    const savedActions = savedValue.Actions || [];
    return {
        ComponentName: savedValue.ComponentName,
        Actions: [...STANDARD_ACTIONS],
        SelectedActions: STANDARD_ACTIONS.filter(a => savedActions.includes(a))
    };
};


const mergeSectionsList = (defaultSections, savedSections) => {
    const saved = savedSections || [];
    const usedSavedIndexes = new Set();

    const merged = defaultSections.map((section, index) => {
        const matchIndex = findSectionMatchIndex(saved, section, index);
        const matchedSaved = matchIndex !== -1 ? saved[matchIndex] : null;
        if (matchIndex !== -1) usedSavedIndexes.add(matchIndex);
        const wrapped = mergePermissionsWithDefaults({ temp: section }, { temp: matchedSaved });
        return wrapped.temp;
    });

    saved.forEach((s, idx) => {
        if (usedSavedIndexes.has(idx)) return;
        const key = s.ComponentName || s.title;
        const alreadyExists = merged.some(m => (m.ComponentName || m.title) === key);
        if (alreadyExists) return;
        const built = buildFromSavedOnly(s);
        if (built) merged.push(built);
    });

    return merged;
};


const mergePermissionsWithDefaults = (defaultObj, savedObj) => {
    const result = {};
    const saved = savedObj || {};
    const defaultKeys = new Set(Object.keys(defaultObj));

    for (const [key, defValue] of Object.entries(defaultObj)) {
        const savedValue = saved[key];
        if (defValue.Sections) {
            result[key] = {
                title: defValue.title,
                ...(defValue.ComponentName ? { ComponentName: defValue.ComponentName } : {}),
                Sections: mergeSectionsList(defValue.Sections, savedValue && savedValue.Sections)
            };
        } else {
            const savedActions = (savedValue && savedValue.Actions) || [];
            result[key] = {
                ComponentName: defValue.ComponentName,
                Actions: [...defValue.Actions],
                SelectedActions: defValue.Actions.filter(a => savedActions.includes(a))
            };
        }
    }


    for (const [key, savedValue] of Object.entries(saved)) {
        if (defaultKeys.has(key)) continue;
        const built = buildFromSavedOnly(savedValue);
        if (built) result[key] = built;
    }

    return result;
};


const filterEmptyPermissions = (obj) => {
    if (obj.Sections) {
        const filteredSections = obj.Sections
            .map(s => filterEmptyPermissions(s))
            .filter(s => s !== null);
        if (filteredSections.length === 0) return null;
        return { ...obj, Sections: filteredSections };
    }
    if (!obj.Actions || obj.Actions.length === 0) return null;
    return obj;
};

const getVisiblePermissionEntries = (permsObj) => {
    return Object.entries(permsObj)
        .map(([key, value]) => [key, filterEmptyPermissions(value)])
        .filter(([, value]) => value !== null);
};

const getDefaultPermissions = () => ({
    Profile: { ComponentName: "My Profile", Actions: ['View'], SelectedActions: ['View'] },
    Dashboard: { ComponentName: "Dashboard", Actions: ['View'], SelectedActions: [] },
    Main: {
        title: "Main Components", Sections: [
            {
                title: "Masters", Sections: [
                    {
                        ComponentName: "All Masters",
                        Actions: ["View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "Active Masters",
                        Actions: ["View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "Inactive Masters",
                        Actions: ["View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    }
                ]
            },
            {
                title: "Users", Sections: [
                    {
                        ComponentName: "All Users",
                        Actions: ["View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "Active Users",
                        Actions: ["View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "Inactive Users",
                        Actions: ["View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    }
                ]
            },
            {
                ComponentName: "Plans",
                Actions: ["Add", "View", "Edit", "Delete", "Download"],
                SelectedActions: []
            }
        ]
    },
    Support: {
        title: "Support", Sections: [
            {
                title: "Complaints", Sections: [
                    {
                        ComponentName: "Complaint Types",
                        Actions: ["Add", "View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "All Complaints",
                        Actions: ["Reply", "View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "Pending Complaints",
                        Actions: ["Reply", "View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "In Progress Complaints",
                        Actions: ["Reply", "View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    },
                    {
                        ComponentName: "Resolved Complaints",
                        Actions: ["Reply", "View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    }
                ]
            },
            {
                title: "Customer Support", Sections: [
                    {
                        ComponentName: "Support Types",
                        Actions: ["Add", "View", "Edit", "Delete", "Download"],
                        SelectedActions: []
                    }
                ]
            }
        ]
    }
});

const findComponent = (obj, path) => {
    if (obj.ComponentName === path) return obj;
    if (obj.Sections) for (const s of obj.Sections) { const f = findComponent(s, path); if (f) return f; }
    if (obj[path]) return obj[path];
    return null;
};

// Uses POST because browsers block GET with body
const fetchRoleById = async (id) => {
    const res = await fetch(`${API_BASE}/roles/getrolebyid`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ id })
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Failed to fetch role (${res.status})`);
    }
    return res.json();
};

export const AddEditRole = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const [formData, setFormData] = useState({ roleName: '', status: 'active', rolesAndPermission: {} });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);

    useEffect(() => {
        if (isEdit) {
            setFetching(true);
            fetchRoleById(id)
                .then(data => {
                    if (data.success && data.role) {
                        const savedPerms = unwrapPermissions(data.role.rolesAndPermission);
                        setFormData({
                            roleName: data.role.roleName || '',
                            status: data.role.status || 'active',
                            rolesAndPermission: mergePermissionsWithDefaults(getDefaultPermissions(), savedPerms)
                        });
                    }
                })
                .catch(err => setApiResponse({ success: false, error: err.message }))
                .finally(() => setFetching(false));
        } else {
            setFormData(prev => ({ ...prev, rolesAndPermission: getDefaultPermissions() }));
        }
    }, [id, isEdit]);

    const validate = () => {
        const e = {};
        if (!formData.roleName.trim()) e.roleName = 'Role name is required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const togglePermission = (path, action) => {
        setFormData(prev => {
            const np = JSON.parse(JSON.stringify(prev.rolesAndPermission));
            let comp = null;
            for (const k in np) { comp = findComponent(np[k], path); if (comp) break; }
            if (comp) {
                if (!comp.SelectedActions) comp.SelectedActions = [];
                if (action === 'All') {
                    const avail = (comp.Actions || []).filter(a => a !== 'All');
                    comp.SelectedActions = avail.every(a => comp.SelectedActions.includes(a)) ? [] : [...avail];
                } else {
                    const idx = comp.SelectedActions.indexOf(action);
                    idx === -1 ? comp.SelectedActions.push(action) : comp.SelectedActions.splice(idx, 1);
                }
            }
            return { ...prev, rolesAndPermission: np };
        });
    };

    const selectAllPermissions = (path) => {
        setFormData(prev => {
            const np = JSON.parse(JSON.stringify(prev.rolesAndPermission));
            let comp = null;
            for (const k in np) { comp = findComponent(np[k], path); if (comp) break; }
            if (comp) comp.SelectedActions = (comp.Actions || []).filter(a => a !== 'All');
            return { ...prev, rolesAndPermission: np };
        });
    };

    const clearAllPermissions = (path) => {
        setFormData(prev => {
            const np = JSON.parse(JSON.stringify(prev.rolesAndPermission));
            let comp = null;
            for (const k in np) { comp = findComponent(np[k], path); if (comp) break; }
            if (comp) comp.SelectedActions = [];
            return { ...prev, rolesAndPermission: np };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        setApiResponse(null);
        try {
            const payload = {
                roleName: formData.roleName,
                rolesAndPermission: wrapPermissions(transformForAPI(formData.rolesAndPermission)),
                status: formData.status
            };
            const url = isEdit ? `${API_BASE}/roles/updaterole/${id}` : `${API_BASE}/roles/addrole`;
            const method = isEdit ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
            if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || `Failed (${res.status})`); }
            const data = await res.json();
            setApiResponse({ success: true, data });
            setTimeout(() => { setLoading(false); navigate('/admin/roles'); }, 800);
        } catch (err) {
            setApiResponse({ success: false, error: err.message });
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Are you sure you want to delete this role?')) return;
        try {
            const res = await fetch(`${API_BASE}/roles/deleterole/${id}`, { method: 'DELETE', headers });
            if (!res.ok) throw new Error('Delete failed');
            navigate('/admin/roles');
        } catch (err) {
            setApiResponse({ success: false, error: err.message });
        }
    };

    const countPermissions = (obj) => {
        let c = 0;
        if (obj.SelectedActions) c += obj.SelectedActions.length;
        if (obj.Sections) obj.Sections.forEach(s => { c += countPermissions(s); });
        return c;
    };
    const totalPermissions = Object.values(formData.rolesAndPermission).reduce((a, c) => a + countPermissions(c), 0);

    // Only components/sections that actually have at least one available action get rendered
    const visiblePermissionEntries = getVisiblePermissionEntries(formData.rolesAndPermission);

    if (fetching) return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center rounded-2xl">
            <div className="flex flex-col items-center gap-3"><Loader2 size={32} className="animate-spin text-orange-500" /><p className="text-sm text-gray-500">Loading role...</p></div>
        </div>
    );

    return (
        <div className="space-y-6 bg-gray-50 min-h-screen p-4 rounded-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/admin/roles')} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100"><ArrowLeft size={16} /> Back</button>
                    <div><h1 className="text-lg sm:text-xl font-bold text-gray-800">{isEdit ? 'Edit Role' : 'Add New Role'}</h1>
                        <p className="text-xs sm:text-sm mt-0.5 text-gray-500">{isEdit ? 'Update role information and permissions' : 'Create a new role with permissions'}</p></div>
                </div>
                {isEdit && <button onClick={handleDelete} className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl bg-red-50 text-red-500 border border-red-200 hover:bg-red-100"><Trash2 size={16} /> Delete</button>}
            </div>

            <Panel className="p-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Role Name *</label>
                            <div className="relative"><Shield size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input type="text" value={formData.roleName} onChange={e => setFormData({ ...formData, roleName: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl bg-orange-50 border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none" placeholder="e.g., staff-1" /></div>
                            {errors.roleName && <p className="mt-1 text-xs text-red-500">{errors.roleName}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1.5 text-gray-600">Status</label>
                            <div className="relative"><Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
                                    className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl appearance-none bg-orange-50 border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none">
                                    <option value="active">Active</option><option value="inactive">Inactive</option></select></div>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-orange-200">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-3">
                            <div className="flex items-center gap-2"><Settings size={18} className="text-orange-500" /><h3 className="text-sm font-semibold text-gray-800">Component Permissions</h3>
                                <span className="text-xs text-gray-500">({totalPermissions} assigned)</span></div>
                            <div className="flex gap-2 flex-wrap">
                                <button type="button" onClick={() => { const np = JSON.parse(JSON.stringify(formData.rolesAndPermission)); const fn = o => { if (o.Actions) o.SelectedActions = o.Actions.filter(a => a !== 'All'); if (o.Sections) o.Sections.forEach(fn); }; Object.values(np).forEach(fn); setFormData(p => ({ ...p, rolesAndPermission: np })); }}
                                    className="text-xs px-3 py-1 rounded-lg bg-orange-50 text-orange-500 border border-orange-200 hover:bg-orange-100 font-medium">Select All</button>
                                <button type="button" onClick={() => { const np = JSON.parse(JSON.stringify(formData.rolesAndPermission)); const fn = o => { o.SelectedActions = []; if (o.Sections) o.Sections.forEach(fn); }; Object.values(np).forEach(fn); setFormData(p => ({ ...p, rolesAndPermission: np })); }}
                                    className="text-xs px-3 py-1 rounded-lg bg-gray-50 text-gray-500 border border-gray-200 hover:bg-gray-100 font-medium">Clear All</button>
                            </div>
                        </div>
                        <div className="space-y-3">
                            {visiblePermissionEntries.map(([key, value]) => (
                                <PermissionSelector key={key} componentKey={key} componentData={value} onToggle={togglePermission} onSelectAll={selectAllPermissions} onClearAll={clearAllPermissions} />
                            ))}
                        </div>
                        <div className="mt-3 p-3 rounded-xl bg-orange-50 border border-orange-200">
                            <p className="text-xs text-gray-600"><span className="font-semibold text-orange-500">Note:</span> Only listed actions can be toggled. Select &quot;All&quot; for full access.</p>
                        </div>
                    </div>

                    {apiResponse && <div className={`p-3 rounded-xl ${apiResponse.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
                        <p className={`text-sm ${apiResponse.success ? 'text-green-700' : 'text-red-700'}`}>{apiResponse.success ? `✓ Role ${isEdit ? 'updated' : 'created'}!` : `✗ ${apiResponse.error}`}</p></div>}

                    <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-orange-200">
                        <button type="button" onClick={() => navigate('/admin/roles')} className="px-5 py-2.5 text-sm font-medium rounded-xl text-gray-600 hover:bg-gray-50">Cancel</button>
                        <button type="submit" disabled={loading} className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-xl disabled:opacity-70 bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm">
                            {loading ? <><Loader2 size={16} className="animate-spin" /> Saving...</> : <><Save size={16} /> {isEdit ? 'Update Role' : 'Create Role'}</>}
                        </button>
                    </div>
                </form>
            </Panel>
        </div>
    );
};

export default AddEditRole;